import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  MapLayerType,
  LayerConfig,
  DEFAULT_LAYER_CONFIG,
  LAYER_GROUPS,
  ALL_MAP_LAYERS,
} from '@/types/map';
import { getDefaultNWSLayerVisibility, NWS_LAYER_GROUPS } from '@/config/nws-layers';

export type BaseLayerType =
  | 'streets'
  | 'satellite'
  | 'hybrid'
  | 'topo'
  | 'terrain'
  | 'lightGray'
  | 'darkGray'
  | 'navigation'
  | 'natGeo'
  | 'oceans';

interface MapLayersState {
  // Layer visibility and configuration
  layers: Record<MapLayerType, LayerConfig>;

  // Base layer (various Esri basemap styles)
  baseLayer: BaseLayerType;

  // Weather and overlay layer visibility
  isRadarLayerVisible: boolean;
  isLightningLayerVisible: boolean;
  isRiverGaugesLayerVisible: boolean;
  isWindLayerVisible: boolean;
  isEarthquakeLayerVisible: boolean;
  isActiveFiresLayerVisible: boolean;
  isAirQualityLayerVisible: boolean;
  isSPCOutlooksLayerVisible: boolean;
  isWebcamsLayerVisible: boolean;
  isIncidentGeometryVisible: boolean;

  // Flood forecast layers
  isFloodForecastLayerVisible: boolean;
  isFloodZonesLayerVisible: boolean;
  isFloodInundationLayerVisible: boolean;
  isStormSurgeLayerVisible: boolean;
  isPredictedRoadClosuresLayerVisible: boolean;
  isPredictedDamageLayerVisible: boolean;
  floodForecastTimelineHours: number; // 0-168 (7 days)

  // NWS layer visibility (keyed by layer ID from nws-layers.ts)
  nwsLayerVisibility: Record<string, boolean>;

  // Radar configuration
  radarAnimationSpeed: number; // milliseconds between frames
  radarOpacity: number; // 0-1 opacity for radar layer

  // Per-layer opacity (0-1, keyed by MapLayerType)
  layerOpacity: Record<string, number>;

  // Layer render order (ordered array of MapLayerType, first = bottom)
  layerOrder: string[];

  // Layer group expand/collapse state (persisted)
  layerGroupExpanded: Record<string, boolean>;

  // Actions
  toggleLayer: (layer: MapLayerType) => void;
  setLayerVisibility: (layer: MapLayerType, visible: boolean) => void;
  setBaseLayer: (baseLayer: BaseLayerType) => void;
  toggleRadarLayer: () => void;
  setRadarAnimationSpeed: (speed: number) => void;
  setRadarOpacity: (opacity: number) => void;
  toggleLightningLayer: () => void;
  toggleRiverGaugesLayer: () => void;
  toggleWindLayer: () => void;
  toggleEarthquakeLayer: () => void;
  toggleActiveFiresLayer: () => void;
  toggleAirQualityLayer: () => void;
  toggleSPCOutlooksLayer: () => void;
  toggleWebcamsLayer: () => void;
  toggleIncidentGeometry: () => void;
  toggleFloodForecastLayer: () => void;
  toggleFloodZonesLayer: () => void;
  toggleFloodInundationLayer: () => void;
  toggleStormSurgeLayer: () => void;
  togglePredictedRoadClosuresLayer: () => void;
  togglePredictedDamageLayer: () => void;
  setFloodForecastTimelineHours: (hours: number) => void;
  toggleNWSLayer: (layerId: string) => void;
  setNWSLayerVisibility: (layerId: string, visible: boolean) => void;
  setLayerOpacity: (layer: string, opacity: number) => void;
  setLayerOrder: (order: string[]) => void;
  moveLayerUp: (layer: string) => void;
  moveLayerDown: (layer: string) => void;
  setLayerGroupExpanded: (groupId: string, expanded: boolean) => void;
  toggleLayerGroup: (groupId: string) => void;
  expandAllLayerGroups: () => void;
  collapseAllLayerGroups: () => void;
  resetLayersState: () => void;
}

// Layers rendered client-side with dedicated hooks (not fetched from /api/map/features).
// These must NOT be forwarded to the backend `layers` query param — the backend
// Zod enum does not accept them and will return 400 VALIDATION_ERROR (see MAP-003).
//   - orthophotos: rendered via useOrthophotoLayers.ts from /api/suas endpoints
const CLIENT_ONLY_LAYERS: Set<string> = new Set(['orthophotos']);

// Whitelist of layer keys that currently exist in the MapLayerType union.
// MAP-003 follow-up: the persisted `map-layers-storage` blob can still contain
// layer keys that were removed from the app long ago (`gps_tracks`,
// `hazmat_incidents`, `flood_inundation_extents`, ...). The `migrate()` ladder
// below is mutually exclusive and zustand runs it exactly ONCE, so a profile
// persisted at version <= 4 skips the v5 sanitizer entirely and is re-stamped at
// the current version — after which migrate() never runs again and the orphan
// keys are permanently unreachable by any future migrator.
const VALID_LAYERS: Set<string> = new Set<string>(ALL_MAP_LAYERS);

// Get visible layers as array (excludes client-only layers that have dedicated data sources)
export function getVisibleLayers(layers: Record<MapLayerType, LayerConfig>): MapLayerType[] {
  // Unknown keys MUST be dropped here, not just at migration time. If an orphan
  // key with `visible: true` reaches the `layers` query param of
  // GET /api/map/features, the backend Zod enum rejects the WHOLE request with
  // 400 VALIDATION_ERROR — so the operator's common operating picture loses
  // every layer (incidents, road closures, shelters, damage assessments), not
  // just the orphan, and stays broken until localStorage is cleared by hand.
  return Object.entries(layers)
    .filter(([key, config]) => config?.visible && VALID_LAYERS.has(key) && !CLIENT_ONLY_LAYERS.has(key))
    .map(([layer]) => layer as MapLayerType);
}

// Default/reset basemap is the muted gray canvas so status colors carry the map.
// Computed lazily so non-DOM environments (tests) can import this module.
function defaultBaseLayer(): BaseLayerType {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
    ? 'darkGray'
    : 'lightGray';
}

const INITIAL_LAYERS_STATE = {
  layers: { ...DEFAULT_LAYER_CONFIG },
  baseLayer: 'lightGray' as BaseLayerType,
  isRadarLayerVisible: false,
  isLightningLayerVisible: false,
  isRiverGaugesLayerVisible: false,
  isWindLayerVisible: false,
  isEarthquakeLayerVisible: false,
  isActiveFiresLayerVisible: false,
  isAirQualityLayerVisible: false,
  isSPCOutlooksLayerVisible: false,
  isWebcamsLayerVisible: false,
  isIncidentGeometryVisible: true,
  isFloodForecastLayerVisible: false,
  isFloodZonesLayerVisible: false,
  isFloodInundationLayerVisible: false,
  isStormSurgeLayerVisible: false,
  isPredictedRoadClosuresLayerVisible: false,
  isPredictedDamageLayerVisible: false,
  floodForecastTimelineHours: 0,
  nwsLayerVisibility: getDefaultNWSLayerVisibility(),
  radarAnimationSpeed: 500,
  radarOpacity: 0.7,
  layerOpacity: {} as Record<string, number>,
  layerOrder: [] as string[],
  layerGroupExpanded: {} as Record<string, boolean>,
};

export const useMapLayersStore = create<MapLayersState>()(
  persist(
    (set) => ({
      ...INITIAL_LAYERS_STATE,

      toggleLayer: (layer) =>
        set((state) => {
          // Layer entry may be missing if it was added after this user's persisted
          // store was created. Fall back to DEFAULT_LAYER_CONFIG defensively.
          const current = state.layers[layer] ?? DEFAULT_LAYER_CONFIG[layer];
          if (!current) return state;
          return {
            layers: { ...state.layers, [layer]: { ...current, visible: !current.visible } },
          };
        }),
      setLayerVisibility: (layer, visible) =>
        set((state) => {
          const current = state.layers[layer] ?? DEFAULT_LAYER_CONFIG[layer];
          if (!current) return state;
          return {
            layers: { ...state.layers, [layer]: { ...current, visible } },
          };
        }),
      setBaseLayer: (baseLayer) => set({ baseLayer }),
      toggleRadarLayer: () => set((state) => ({ isRadarLayerVisible: !state.isRadarLayerVisible })),
      setRadarAnimationSpeed: (speed) => set({ radarAnimationSpeed: speed }),
      setRadarOpacity: (opacity) => set({ radarOpacity: Math.max(0, Math.min(1, opacity)) }),
      toggleLightningLayer: () => set((state) => ({ isLightningLayerVisible: !state.isLightningLayerVisible })),
      toggleRiverGaugesLayer: () => set((state) => ({ isRiverGaugesLayerVisible: !state.isRiverGaugesLayerVisible })),
      toggleWindLayer: () => set((state) => ({ isWindLayerVisible: !state.isWindLayerVisible })),
      toggleEarthquakeLayer: () => set((state) => ({ isEarthquakeLayerVisible: !state.isEarthquakeLayerVisible })),
      toggleActiveFiresLayer: () => set((state) => ({ isActiveFiresLayerVisible: !state.isActiveFiresLayerVisible })),
      toggleAirQualityLayer: () => set((state) => ({ isAirQualityLayerVisible: !state.isAirQualityLayerVisible })),
      toggleSPCOutlooksLayer: () => set((state) => ({ isSPCOutlooksLayerVisible: !state.isSPCOutlooksLayerVisible })),
      toggleWebcamsLayer: () => set((state) => ({ isWebcamsLayerVisible: !state.isWebcamsLayerVisible })),
      toggleIncidentGeometry: () => set((state) => ({ isIncidentGeometryVisible: !state.isIncidentGeometryVisible })),
      toggleFloodForecastLayer: () => set((state) => ({ isFloodForecastLayerVisible: !state.isFloodForecastLayerVisible })),
      toggleFloodZonesLayer: () => set((state) => ({ isFloodZonesLayerVisible: !state.isFloodZonesLayerVisible })),
      toggleFloodInundationLayer: () => set((state) => ({ isFloodInundationLayerVisible: !state.isFloodInundationLayerVisible })),
      toggleStormSurgeLayer: () => set((state) => ({ isStormSurgeLayerVisible: !state.isStormSurgeLayerVisible })),
      togglePredictedRoadClosuresLayer: () => set((state) => ({ isPredictedRoadClosuresLayerVisible: !state.isPredictedRoadClosuresLayerVisible })),
      togglePredictedDamageLayer: () => set((state) => ({ isPredictedDamageLayerVisible: !state.isPredictedDamageLayerVisible })),
      setFloodForecastTimelineHours: (hours) => set({ floodForecastTimelineHours: Math.max(0, Math.min(168, hours)) }),
      toggleNWSLayer: (layerId) =>
        set((state) => ({
          nwsLayerVisibility: { ...state.nwsLayerVisibility, [layerId]: !state.nwsLayerVisibility[layerId] },
        })),
      setNWSLayerVisibility: (layerId, visible) =>
        set((state) => ({
          nwsLayerVisibility: { ...state.nwsLayerVisibility, [layerId]: visible },
        })),
      setLayerOpacity: (layer, opacity) =>
        set((state) => ({
          layerOpacity: { ...state.layerOpacity, [layer]: Math.max(0, Math.min(1, opacity)) },
        })),
      setLayerOrder: (order) => set({ layerOrder: order }),
      moveLayerUp: (layer) =>
        set((state) => {
          const idx = state.layerOrder.indexOf(layer);
          if (idx < state.layerOrder.length - 1) {
            const newOrder = [...state.layerOrder];
            [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
            return { layerOrder: newOrder };
          }
          return state;
        }),
      moveLayerDown: (layer) =>
        set((state) => {
          const idx = state.layerOrder.indexOf(layer);
          if (idx > 0) {
            const newOrder = [...state.layerOrder];
            [newOrder[idx], newOrder[idx - 1]] = [newOrder[idx - 1], newOrder[idx]];
            return { layerOrder: newOrder };
          }
          return state;
        }),
      setLayerGroupExpanded: (groupId, expanded) =>
        set((state) => ({
          layerGroupExpanded: { ...state.layerGroupExpanded, [groupId]: expanded },
        })),
      toggleLayerGroup: (groupId) =>
        set((state) => ({
          layerGroupExpanded: { ...state.layerGroupExpanded, [groupId]: !state.layerGroupExpanded[groupId] },
        })),
      expandAllLayerGroups: () => {
        const expanded: Record<string, boolean> = {};
        LAYER_GROUPS.forEach((g) => { expanded[g.id] = true; });
        NWS_LAYER_GROUPS.forEach((g) => { expanded[`nws-${g.id}`] = true; });
        set({ layerGroupExpanded: expanded });
      },
      collapseAllLayerGroups: () => {
        const collapsed: Record<string, boolean> = {};
        LAYER_GROUPS.forEach((g) => { collapsed[g.id] = false; });
        NWS_LAYER_GROUPS.forEach((g) => { collapsed[`nws-${g.id}`] = false; });
        set({ layerGroupExpanded: collapsed });
      },
      resetLayersState: () => set({ ...INITIAL_LAYERS_STATE, baseLayer: defaultBaseLayer() }),
    }),
    {
      name: 'map-layers-storage',
      version: 9,
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Partial<MapLayersState>;
        if (version === 0) {
          return {
            ...state,
            isWindLayerVisible: state.isWindLayerVisible ?? false,
            isEarthquakeLayerVisible: state.isEarthquakeLayerVisible ?? false,
            isActiveFiresLayerVisible: state.isActiveFiresLayerVisible ?? false,
            isAirQualityLayerVisible: state.isAirQualityLayerVisible ?? false,
            isSPCOutlooksLayerVisible: state.isSPCOutlooksLayerVisible ?? false,
            isWebcamsLayerVisible: state.isWebcamsLayerVisible ?? false,
            isFloodForecastLayerVisible: false,
            isFloodZonesLayerVisible: false,
            isFloodInundationLayerVisible: false,
            floodForecastTimelineHours: 0,
            layers: { ...DEFAULT_LAYER_CONFIG, ...state.layers },
          };
        }
        if (version === 1) {
          return {
            ...state,
            isFloodForecastLayerVisible: false,
            isFloodZonesLayerVisible: false,
            isFloodInundationLayerVisible: false,
            floodForecastTimelineHours: 0,
          };
        }
        if (version === 2) {
          // Rename fire_resources → equipment in persisted layers
          const layers = { ...state.layers } as Record<string, unknown>;
          if ('fire_resources' in layers) {
            layers['equipment'] = layers['fire_resources'];
            delete layers['fire_resources'];
          }
          return { ...state, layers };
        }
        if (version === 3) {
          return {
            ...state,
            isStormSurgeLayerVisible: false,
            isPredictedRoadClosuresLayerVisible: false,
            isPredictedDamageLayerVisible: false,
          };
        }
        if (version === 4) {
          // Backfill new flood layer config entries (3 FIM products) so toggle
          // handlers can read state.layers[<key>] without crashing.
          const existing = (state.layers ?? {}) as Record<string, unknown>;
          return {
            ...state,
            layers: { ...DEFAULT_LAYER_CONFIG, ...existing },
          };
        }
        if (version === 5) {
          // MAP-003: Strip orphan layer keys from persisted state.
          // Past versions of the app shipped layer types (e.g. `flood_inundation_extents`,
          // `gps_tracks`, `hazmat_incidents`, `fire_resources`) that were later removed
          // from the MapLayerType union. Users with stale localStorage still had these
          // keys in `state.layers`, and `getVisibleLayers()` happily included them in
          // the GET /api/map/features `layers` query param — the backend Zod enum
          // rejected the request with 400 VALIDATION_ERROR, breaking the map.
          // Rebuild `state.layers` from DEFAULT_LAYER_CONFIG, copying ONLY the visible
          // flag for keys that exist in the current MapLayerType union.
          const existing = (state.layers ?? {}) as Record<string, Partial<LayerConfig>>;
          const sanitized = { ...DEFAULT_LAYER_CONFIG };
          for (const layer of ALL_MAP_LAYERS) {
            const persisted = existing[layer];
            if (persisted && typeof persisted.visible === 'boolean') {
              sanitized[layer] = { ...DEFAULT_LAYER_CONFIG[layer], visible: persisted.visible };
            }
          }
          return { ...state, layers: sanitized };
        }
        if (version === 6) {
          // The field-ops map previously force-hid every operational layer
          // except 'incidents' on mount, writing those `false` values into this
          // shared persisted store — so layers like Damage Assessments showed as
          // OFF on every map even though their default is on. Reset the
          // operational data layers to their default visibility to undo that
          // pollution; non-operational layers (weather, drones, etc.) keep the
          // user's persisted choice.
          const OPERATIONAL_LAYERS: MapLayerType[] = [
            'damage_assessments',
            'road_closures',
            'utility_outages',
            'critical_infrastructure',
            'supply_requests',
            'tasks',
            'shelters',
          ];
          const existing = (state.layers ?? {}) as Record<string, Partial<LayerConfig>>;
          const layers = { ...DEFAULT_LAYER_CONFIG, ...existing } as Record<MapLayerType, LayerConfig>;
          for (const layer of OPERATIONAL_LAYERS) {
            layers[layer] = { ...DEFAULT_LAYER_CONFIG[layer] };
          }
          return { ...state, layers };
        }
        if (version === 7) {
          // Color-budget redesign: 'streets' was the app default (persisted for
          // everyone, not a deliberate choice), so migrate it to the muted gray
          // canvas once. Users who re-pick streets from the basemap selector
          // keep it (they'll be at version 8).
          const migrated = { ...state, layers: { ...DEFAULT_LAYER_CONFIG, ...state.layers } };
          if (state.baseLayer === 'streets') {
            return { ...migrated, baseLayer: defaultBaseLayer() };
          }
          return migrated;
        }
        if (version === 8) {
          // Wind-probability layers (tropical_wsp_34kt / _64kt) are new keys:
          // fill them in from the defaults and keep every persisted layer pref.
          return { ...state, layers: { ...DEFAULT_LAYER_CONFIG, ...state.layers } };
        }
        return state as MapLayersState;
      },
      partialize: (state) => ({
        layers: state.layers,
        baseLayer: state.baseLayer,
        isRadarLayerVisible: state.isRadarLayerVisible,
        radarAnimationSpeed: state.radarAnimationSpeed,
        radarOpacity: state.radarOpacity,
        isLightningLayerVisible: state.isLightningLayerVisible,
        isRiverGaugesLayerVisible: state.isRiverGaugesLayerVisible,
        isWindLayerVisible: state.isWindLayerVisible,
        isEarthquakeLayerVisible: state.isEarthquakeLayerVisible,
        isActiveFiresLayerVisible: state.isActiveFiresLayerVisible,
        isAirQualityLayerVisible: state.isAirQualityLayerVisible,
        isSPCOutlooksLayerVisible: state.isSPCOutlooksLayerVisible,
        isWebcamsLayerVisible: state.isWebcamsLayerVisible,
        isIncidentGeometryVisible: state.isIncidentGeometryVisible,
        isFloodForecastLayerVisible: state.isFloodForecastLayerVisible,
        isFloodZonesLayerVisible: state.isFloodZonesLayerVisible,
        isFloodInundationLayerVisible: state.isFloodInundationLayerVisible,
        isStormSurgeLayerVisible: state.isStormSurgeLayerVisible,
        isPredictedRoadClosuresLayerVisible: state.isPredictedRoadClosuresLayerVisible,
        isPredictedDamageLayerVisible: state.isPredictedDamageLayerVisible,
        floodForecastTimelineHours: 0, // F22: always reset to "Now" on reload
        nwsLayerVisibility: state.nwsLayerVisibility,
        layerOpacity: state.layerOpacity,
        layerOrder: state.layerOrder,
        layerGroupExpanded: state.layerGroupExpanded,
      }),
    }
  )
);
